import z from 'zod';
import {
  emailSchema,
  nameSchema,
  optionalStringSchema,
  phoneSchema,
  priceSchema,
  quantitySchema,
} from '../validation';
import {
  itemPackingSchema,
  itemTypeSchema,
  temperatureSchema,
  vehicleTypesSchema,
  weightSchema,
} from '.';
import { orderPODCopySettingSchema } from '../model/order/order.settings.model';

const contactPersonSchema = z.object({
  /** the email of the contact.  */
  email: emailSchema.optional(),
  /** the phone number of the contact if possible*/
  phone: phoneSchema.optional(),
  /** the name of the contact if possible */
  name: nameSchema.optional(),
});

/** this client settings are used to customize the client's settings for the app */
export const clientSettingsSchema = z.object({
  // ==== ITEMS SETTINGS ====
  /** the default item name that will be used in order. */
  item_default_name: optionalStringSchema(nameSchema).optional(),

  /** the default item type that will be used in order. */
  item_default_type: itemTypeSchema.optional(),

  /** the default cost of the item that will be used in order */
  item_default_cost: priceSchema.optional(),

  /** the default item weight in TONs. */
  item_default_weight: weightSchema.optional(),

  /** the default packing type (pallet, package, other). */
  item_default_packing: itemPackingSchema.optional(),

  /** the default quantity of items per vehicle. */
  item_default_quantity: quantitySchema.optional(),

  /** the default temperature preference for chilled/frozen vehicles. */
  item_default_temperature: temperatureSchema.optional(),

  // ==== Vehicle SETTINGS ====

  /** the default vehicle type that will be used in order.
   *
   * By default, if we have to choose one,,, we will always choose the first element in the array.
   */
  vehicle_preferred_types: z.array(vehicleTypesSchema).optional(),

  /** the customer proof of delivery (POD) settings */
  pod_settings: orderPODCopySettingSchema.optional(),

  // ==== Contacts ====

  /** the contacts that will be used to send financial reports to such as invoices.*/
  contacts_financial_reports: z.array(contactPersonSchema).optional(),

  /** the contacts that will be used to send operation reports to such as orders.*/
  contacts_operation_reports: z.array(contactPersonSchema).optional(),

  // ==== Invoicing ====

  /**
   * When `true`, delivered legs against this client automatically get
   * a per-leg invoice (capture flow runs on the
   * `order.delivered` event). When `false`, the leg stays in the
   * "Eligible orders" queue until an admin runs the manual
   * "Create invoice from selected legs" flow from `ClientDrawer`.
   *
   * Defaults to `false`. Resolution order (first match wins) at the
   * `OrderEventsService.onDelivered` event:
   *   1. `Account.settings.allow_auto_invoice_capture` (per-pair override)
   *   2. `Agreement.terms.payment.invoiceIssuance`
   *   3. `Client.settings.allow_auto_invoice_capture` (this field)
   *   4. `Org.settings.allow_auto_invoice_capture` (buyer org default)
   *   5. `false`
   */
  allow_auto_invoice_capture: z.boolean().optional(),
});

export type ClientSettings = z.infer<typeof clientSettingsSchema>;
